import { useWindowSize } from "@react-hookz/web";
import { skipToken } from "@reduxjs/toolkit/query";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Drawer, DropdownMenu, Icon, Spinner, Typography } from "@/components/atoms";
import { getTripLogGuestViewUrl } from "@/helpers/bookingHelpers";
import { getErrorMessages } from "@/helpers/reduxHelpers";
import { bookingApi } from "@/redux/apis/booking/bookingApi";
import {
  useCancelClientBookingMutation,
  useEmailInvoiceClientBookingMutation,
  useGetClientBookingDataQuery,
  useLazyGetClientBookingDataQuery,
  useLazyGetClientBookingQuery,
} from "@/redux/apis/clientBooking/clientBookingApi";
import { ClientBooking } from "@/redux/apis/clientBooking/types";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { accountIdSelector } from "@/redux/slices/account/selectors";
import { initBookingScope, initialState, restoreBookingDraft } from "@/redux/slices/clientBooking/clientBookingSlice";
import { buildRebookDraft, mapBookingDetailToRebookSource } from "@/redux/slices/clientBooking/rebookDraft";
import { addToast } from "@/utils";
import { getBookingStatusGroup } from "../statusGroups";
import { CancelBookingModal } from "./CancelBookingModal";

interface BookingActionProps {
  booking: ClientBooking;
  /**
   * Base path for opening/creating a booking. Self-service defaults to the client portal
   * (`/client/booking`); the operator client-record passes its scoped path
   * (`/operator/:op/clients/:clientId/booking`) so the actions stay inside the operator shell.
   */
  bookingBasePath?: string;
  /** Managed client's account id under operator OBO — scopes the detail/reference fetches. Undefined self-service. */
  accountId?: string;
}

type ActionItem = { icon: string; label: string; onClick?: () => void };

const getActions = (
  booking: ClientBooking,
  navigate: ReturnType<typeof useNavigate>,
  bookingBasePath: string,
  isOperator: boolean,
  onReplicate: () => void,
  onCancel: () => void,
  onTrack: () => void,
  onEmailInvoice: () => void
): ActionItem[] => {
  const group = getBookingStatusGroup(booking.status);
  const isUpcoming = group === "upcoming";
  const isInProgress = group === "in_progress";
  // "Track Your Vehicle" only makes sense once a driver is actually en route / on the trip — that's
  // when the backend has a live lat/lng/heading to plot (per Jodie: in-progress jobs only).
  const canTrack = booking.flags.is_driver_on_way || booking.flags.is_driver_on_trip;
  // "Email Invoice" only when a payment tax invoice actually exists for the job — matches the legacy
  // portal's single gate ($job->isPaymentInvoiceGenerated()). Sending is a no-op otherwise.
  const emailInvoice: ActionItem = { icon: "Sms", label: "Email Invoice", onClick: onEmailInvoice };
  const invoiceActions = booking.flags.is_payment_invoice_generated ? [emailInvoice] : [];

  // One URL for an existing booking — the sheet opens read-only or editable based on the booking's
  // `is_editable`. The label just sets the user's expectation for the row's state.
  const bookingUrl = `${bookingBasePath}/${booking.jobId}`;
  const viewDetails: ActionItem = { icon: "Eye", label: "View Booking Details", onClick: () => navigate(bookingUrl) };
  const editDetails: ActionItem = { icon: "Edit2", label: "Edit Booking Details", onClick: () => navigate(bookingUrl) };
  // DEV-1253: re-book this trip (same route/vehicle/transfer/passengers), fresh date + payment.
  const replicate: ActionItem = { icon: "Copy", label: "Replicate Booking Details", onClick: onReplicate };
  // Opens the public Trip Driver Activity page (live map) in a new tab.
  const trackVehicle: ActionItem = { icon: "Car", label: "Track Your Vehicle", onClick: onTrack };

  // Operator client-record + EA managed rows: the adapter can't populate the client
  // `is_editable`/`is_cancellable` flags (the provider job list doesn't carry them), so use the status
  // group as the proxy — upcoming jobs are editable (mirrors what the client shows when `is_editable` is
  // true), everything else is view-only. Either way the sheet resolves the real mode from the fetched
  // detail, so an upcoming job the backend deems non-editable still opens read-only. Cancel/Track stay
  // out (no live flag to gate them here); Replicate always applies. Email Invoice IS offered on
  // non-upcoming rows when an invoice exists — the old portal showed it to operators viewing a client's
  // history too (same template, gated only on the invoice flag, no viewer check).
  if (isOperator) {
    if (isUpcoming) {
      // Operators/EAs can cancel a client's upcoming booking on-behalf (old-portal parity), gated on the
      // real is_cancellable flag (from the provider job metadata / managed list). Backend re-checks.
      const actions: ActionItem[] = [editDetails];
      if (booking.flags.is_cancellable) actions.push({ icon: "CloseCircle", label: "Cancel Booking", onClick: onCancel });
      actions.push(replicate);
      return actions;
    }
    // Track applies to EA managed rows too: the managed list carries the real driver flags (the operator
    // adapter hardcodes them false, so operators never see it here). The detail fetch runs under the
    // owning client's scope, so the tracking_url resolves.
    return [viewDetails, ...(isInProgress && canTrack ? [trackVehicle] : []), ...invoiceActions, replicate];
  }

  if (isUpcoming) {
    const actions: ActionItem[] = [];
    if (booking.flags.is_editable) actions.push(editDetails);
    if (booking.flags.is_cancellable) actions.push({ icon: "CloseCircle", label: "Cancel Booking", onClick: onCancel });
    actions.push(replicate);
    return actions;
  }

  if (isInProgress) {
    return [viewDetails, ...(canTrack ? [trackVehicle] : []), ...invoiceActions, replicate];
  }

  return [viewDetails, ...invoiceActions, replicate];
};

export const BookingAction = ({ booking, bookingBasePath = "/client/booking", accountId }: BookingActionProps) => {
  const { width } = useWindowSize();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isMobile = width < 768;
  const isOperator = !!accountId;
  // The v3 job-data route scopes by account in the path: operator OBO targets the managed client
  // (`accountId` prop); self-service falls back to the client's own active account.
  const activeAccountId = useAppSelector(accountIdSelector);
  const effectiveAccountId = accountId ?? activeAccountId;
  const [cancelOpen, setCancelOpen] = useState(false);

  const [fetchBooking, { isFetching }] = useLazyGetClientBookingQuery();
  const [fetchBookingData] = useLazyGetClientBookingDataQuery();
  const [cancelBooking, { isLoading: isCancelling }] = useCancelClientBookingMutation();
  const [emailInvoice] = useEmailInvoiceClientBookingMutation();
  // Cancellation policy + fees, shown in the confirm modal. Warm the cache for cancellable rows up
  // front (not only on modal open) so the fees are usually ready by the time the user clicks Cancel —
  // all rows share one request (same cache key), and it's skipped entirely when nothing is cancellable.
  const { data: bookingData, isFetching: policyLoading } = useGetClientBookingDataQuery(
    effectiveAccountId ? { accountId: effectiveAccountId } : skipToken,
    { skip: !cancelOpen && (!!accountId || !booking.flags.is_cancellable) }
  );

  // Track = fetch the booking detail (the list row has no tracking link), pull the job hash out of its
  // `tracking_url` (`{base}/trip/{hash}`), and open the public Trip Driver Activity page in a new tab —
  // same destination the operator dashboard shares (getTripLogGuestViewUrl).
  const handleTrack = async () => {
    try {
      const detail = await fetchBooking({ jobId: booking.jobId, accountId }, true).unwrap();
      const hash = detail.tracking_url?.split("/").filter(Boolean).pop();
      if (!hash) {
        addToast("danger", "Live tracking isn't available for this trip yet.");
        return;
      }
      window.open(getTripLogGuestViewUrl(String(booking.jobId), hash), "_blank", "noopener,noreferrer");
    } catch (err) {
      addToast("danger", getErrorMessages(err as Parameters<typeof getErrorMessages>[0])[0]);
    }
  };

  // Cancel = confirm in the modal, then POST the cancel. The list re-fetches (invalidatesTags) so the
  // row's status flips to Cancelled; the draft/list is otherwise untouched.
  const handleCancel = async () => {
    try {
      await cancelBooking({ jobId: booking.jobId, accountId }).unwrap();
      // The operator client-record history is a DIFFERENT api (bookingApi.getClientJobs, tag "Booking")
      // than cancel's clientBookingApi ("ClientBookings"), and cancel happens in-place (no remount), so
      // invalidate it here to flip the row to Cancelled. EA/self lists share the "ClientBookings" tag and
      // already refetch. Harmless when there's no getClientJobs subscription.
      if (isOperator) dispatch(bookingApi.util.invalidateTags(["Booking"]));
      setCancelOpen(false);
      addToast("success", `Booking ${booking.displayJobId} has been cancelled`);
    } catch (err) {
      addToast("danger", getErrorMessages(err as Parameters<typeof getErrorMessages>[0])[0]);
    }
  };

  // Email Invoice = re-send the booking's payment tax invoice email(s) to the account's payment
  // recipients. The row only offers this when the backend flagged a generated invoice, so a success is
  // expected; any failure (e.g. no successful payment attempt) surfaces as a danger toast.
  const handleEmailInvoice = async () => {
    try {
      await emailInvoice({ jobId: booking.jobId, accountId }).unwrap();
      addToast("success", `The invoice for booking ${booking.displayJobId} has been emailed`);
    } catch (err) {
      addToast("danger", getErrorMessages(err as Parameters<typeof getErrorMessages>[0])[0]);
    }
  };

  // Replicate = fetch the full booking detail (the list row is too thin to rebuild from), resolve the
  // numeric transfer id from the client job-data, build a "duplicate" draft, then open the booking flow.
  const handleReplicate = async () => {
    try {
      const [detail, bookingData] = await Promise.all([
        fetchBooking({ jobId: booking.jobId, accountId }, true).unwrap(),
        // preferCacheValue: job-data rarely changes within a session, so reuse the cached entry.
        fetchBookingData({ accountId: effectiveAccountId }, true).unwrap(),
      ]);
      const source = mapBookingDetailToRebookSource(detail, bookingData?.transfer_types);
      const draft = buildRebookDraft(source, "duplicate");
      // Align the draft's scope to the target sheet FIRST: ClientBooking's mount guard resets the draft when
      // the scope differs, which would wipe the replicated booking on an OBO row (same order as rebook()).
      dispatch(initBookingScope(accountId ?? null));
      dispatch(restoreBookingDraft({ ...initialState, ...draft }));
      navigate(bookingBasePath);
    } catch (err) {
      addToast("danger", getErrorMessages(err as Parameters<typeof getErrorMessages>[0])[0]);
    }
  };

  const actions = getActions(
    booking,
    navigate,
    bookingBasePath,
    isOperator,
    handleReplicate,
    () => setCancelOpen(true),
    handleTrack,
    handleEmailInvoice
  );

  const triggerIcon = isFetching ? <Spinner className="!m-0 h-5 w-5" /> : <Icon name="options" isCustom size="lg" />;

  const cancelModal = (
    <CancelBookingModal
      open={cancelOpen}
      displayJobId={booking.displayJobId}
      policy={bookingData?.cancellation_policy}
      policyLoading={policyLoading}
      onClose={() => setCancelOpen(false)}
      onConfirm={() => void handleCancel()}
      loading={isCancelling}
    />
  );

  if (isMobile) {
    return (
      <>
        <Drawer>
          <button className="inline-flex items-center justify-center text-neutral-black" disabled={isFetching}>
            {triggerIcon}
          </button>
          <div className="flex flex-col gap-2.5 px-4 pb-8 pt-2">
            <Typography variant="title">Select Action</Typography>
            <div className="flex flex-col gap-2.5 pt-2">
              {actions.map((action) => (
                // Close the drawer as the action fires — otherwise Track opens a tab behind it, Email
                // Invoice toasts under it, and Cancel stacks its modal on top of the open drawer. The
                // cancel modal is rendered outside the Drawer, so closing here doesn't unmount it.
                <Drawer.Close asChild key={action.label}>
                  <button onClick={action.onClick} className="flex w-full items-center gap-3 rounded-lg bg-neutral-surface-gray px-4 py-3">
                    <Icon name={action.icon} size="sm" className="flex-shrink-0" />
                    <Typography variant="action">{action.label}</Typography>
                  </button>
                </Drawer.Close>
              ))}
            </div>
            <Drawer.Close asChild>
              <Button variant="tertiary" size="lg" className="mt-2 w-full">
                Close
              </Button>
            </Drawer.Close>
          </div>
        </Drawer>
        {cancelModal}
      </>
    );
  }

  return (
    <>
      <DropdownMenu
        button={
          <DropdownMenu.Button
            aria-label="Booking actions"
            disabled={isFetching}
            className="inline-flex items-center justify-center text-neutral-black"
          >
            {triggerIcon}
          </DropdownMenu.Button>
        }
        isCustomButton
        position="bottom-end"
      >
        {actions.map((action) => (
          <DropdownMenu.Item key={action.label} onClick={action.onClick} className="w-56">
            <Icon name={action.icon} size="sm" className="mr-2.5 flex-shrink-0" />
            <Typography>{action.label}</Typography>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu>
      {cancelModal}
    </>
  );
};
